import React, {useEffect, useState} from 'react';
import {TextField, Tooltip} from '@mui/material';
import PropTypes from 'prop-types';
import {bgColorRequester, bgColorSpender} from '../../_helpers';
import {formatCurrencySeparator, normalizeDecimalSeparator} from '../../../utils/number';

/**
 * TextField to edit a number value, with a small display size, accommodating format for currency separator.
 * A tooltip is displayed on top while hovering and while typing large numbers.
 *
 * @param {FormatCurrencySeparatorTextfield.propTypes} props
 * @return {JSX.Element}
 * @constructor
 */
export function FormatCurrencySeparatorTextfield({
    value = 0,
    disabled,
    onChange,
    minValue = 0,
    mandatoryForRequester,
    mandatoryForSpender,
    highlightMandatoryFieldForRequester,
    highlightMandatoryFieldForSpender,
    separator,
    subtotalDecimals,
    decimalRegex,
    sx,
}) {
    const [open, setOpen] = useState(false);
    const [focused, setFocused] = useState(false);
    const [internalValue, setInternalValue] = useState('');

    // This useEffect formats the value when input is not focused
    useEffect(() => {
        const formattedValue = !focused && value >= minValue ?
            formatCurrencySeparator(value, separator, subtotalDecimals) :
            value?.toString();
        setInternalValue(formattedValue);
    }, [value, separator, subtotalDecimals, focused, minValue]);

    // Converts formatted string back to numeric value when focus is lost
    const handleBlur = () => {
        setFocused(false);
        setOpen(false);
        const normalizedValue = normalizeDecimalSeparator(internalValue.toString());
        const numericValue = Math.max(parseFloat(normalizedValue), minValue);
        onChange(numericValue);
    };

    // Removes the formatting for editing
    const handleFocus = () => {
        setFocused(true);
        setInternalValue(value.toString().replace(/,/g, '.').replace(/[^\d.]/g, ''));
    };

    // Handles input change, ensuring no negative values and handling dot and comma as decimal separator
    const handleChange = (event) => {
        let inputValue = normalizeDecimalSeparator(event.target.value);
        if (decimalRegex) {
            inputValue = handleDecimalsOnValue(inputValue);
        }
        if (parseFloat(inputValue) < minValue) {
            inputValue = minValue.toString();
        }
        setInternalValue(inputValue);
    };

    const handleDecimalsOnValue = (value) => {
        const match = value.match(new RegExp(decimalRegex));
        return match ? match[0] : '';
    };

    return (
        <Tooltip
            title={internalValue}
            open={(open || focused) && internalValue.length > 2}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            placement="top"
            arrow
        >
            <TextField
                sx={{
                    backgroundColor: mandatoryForSpender && highlightMandatoryFieldForSpender ?
                        bgColorSpender :
                        mandatoryForRequester && highlightMandatoryFieldForRequester ?
                            bgColorRequester :
                            null,
                    ...sx,
                }}
                disabled={disabled}
                fullWidth
                value={internalValue}
                onChange={handleChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                variant="outlined"
                size="small"
                onMouseEnter={() => setOpen(true)}
                onMouseLeave={() => setOpen(false)}
            />
        </Tooltip>
    );
}

FormatCurrencySeparatorTextfield.propTypes = {
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    disabled: PropTypes.bool,
    onChange: PropTypes.func,
    minValue: PropTypes.number,
    mandatoryForSpender: PropTypes.bool,
    mandatoryForRequester: PropTypes.bool,
    highlightMandatoryFieldForRequester: PropTypes.bool,
    highlightMandatoryFieldForSpender: PropTypes.bool,
    separator: PropTypes.string,
    subtotalDecimals: PropTypes.number,
    decimalRegex: PropTypes.instanceOf(RegExp),
    sx: PropTypes.object,
};

FormatCurrencySeparatorTextfield.defaultProps = {
    sx: {},
};
